import { api, download } from '@/api/client'
import type {
  ConnectionStatus,
  DeliverySettings,
  DeliveryWindow,
  ProductUnit,
  SupplierProduct,
} from '@/api/suppliers'

export interface SupplierCustomer {
  connectionId: string
  businessId: string
  businessName: string
  vatNumber: string | null
  phone: string | null
  city: string | null
  status: ConnectionStatus
  orderCount: number
  lastOrderAt: string | null
  connectedAt: string | null
}

export interface CustomerInviteResult {
  invited: number
  connected: number
  skipped: number
  details: string[]
}

export interface ImportResult {
  created: number
  updated: number
  skipped: number
  errors: string[]
}

/** Μία γραμμή πελάτη κάτω από ένα άθροισμα του δελτίου συλλογής. */
export interface PickingLine {
  orderId: string
  orderNumber: string
  businessName: string
  quantity: string
  note: string | null
}

export interface PickingItem {
  name: string
  unit: ProductUnit
  totalQuantity: string
  lines: PickingLine[]
}

export interface PickingList {
  date: string
  supplierName: string
  orderCount: number
  customerCount: number
  itemCount: number
  items: PickingItem[]
  undatedIncluded: boolean
  generatedAt: string
}

export interface AvailabilityResult {
  products: SupplierProduct[]
  notifiedBusinesses: number
}

export const supplierPortalApi = {
  customers: () => api.get<SupplierCustomer[]>('/supplier/customers'),
  acceptConnection: (id: string) => api.post(`/supplier/connections/${id}/accept`),
  blockConnection: (id: string) => api.post(`/supplier/connections/${id}/block`),
  invite: (emails: string[], message?: string) =>
    api.post<CustomerInviteResult>('/supplier/invitations', { emails, message }),

  products: (includeInactive = false) =>
    api.get<SupplierProduct[]>(`/supplier/products?includeInactive=${includeInactive}`),
  createProduct: (payload: {
    name: string
    unit: ProductUnit
    sku?: string
    price?: string
    packageSize?: string
  }) => api.post<SupplierProduct>('/supplier/products', payload),
  updateProduct: (id: string, payload: { name?: string; price?: string; isActive?: boolean }) =>
    api.patch<SupplierProduct>(`/supplier/products/${id}`, payload),
  deleteProduct: (id: string) => api.delete<void>(`/supplier/products/${id}`),

  /** Δήλωση έλλειψης. Κενή ημερομηνία σημαίνει «το έχω ξανά». */
  setAvailability: (id: string, unavailableUntil: string | null) =>
    api.put<SupplierProduct>(`/supplier/products/${id}/availability`, { unavailableUntil }),
  setAvailabilityBulk: (productIds: string[], unavailableUntil: string | null) =>
    api.post<AvailabilityResult>('/supplier/products/availability', {
      productIds,
      unavailableUntil,
    }),

  delivery: () => api.get<DeliveryWindow>('/supplier/delivery'),
  updateDelivery: (payload: DeliverySettings) =>
    api.patch<DeliveryWindow>('/supplier/delivery', payload),

  pickingList: (date?: string) =>
    api.get<PickingList>(`/supplier/picking-list${date ? `?date=${date}` : ''}`),
  downloadPickingList: (date: string) =>
    download(`/supplier/picking-list.pdf?date=${date}`, `deltio-syllogis-${date}.pdf`),

  /** Η μαζική εισαγωγή στέλνει multipart, όχι JSON. */
  importProducts: async (file: File): Promise<ImportResult> => {
    const form = new FormData()
    form.append('file', file)
    const response = await fetch('/api/v1/supplier/products/import', {
      method: 'POST',
      credentials: 'include',
      headers: authHeader(),
      body: form,
    })
    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw Object.assign(new Error('import failed'), {
        name: 'ApiError',
        status: response.status,
        code: body?.error?.code ?? 'internal_error',
      })
    }
    return (await response.json()) as ImportResult
  },
}

/**
 * Το FormData δεν περνά από τον κοινό client, οπότε η κεφαλίδα μπαίνει εδώ.
 * Ένα ρητό Content-Type θα έσπαγε το boundary που ορίζει ο browser.
 */
let tokenGetter: () => string | null = () => null

export function setImportTokenGetter(getter: () => string | null) {
  tokenGetter = getter
}

function authHeader(): Record<string, string> {
  const token = tokenGetter()
  return token ? { Authorization: `Bearer ${token}` } : {}
}
